import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useUserRoleContext } from '../hooks/useUserRoleContext';
import type { WpSiteListItem, SyncStatus } from '@audit/shared-types';

const STATUS_COLOURS: Record<string, string> = {
  complete: 'bg-green-100 text-green-700',
  partial: 'bg-orange-100 text-orange-700',
  running: 'bg-yellow-100 text-yellow-700',
  pending: 'bg-gray-100 text-gray-600',
  failed: 'bg-red-100 text-red-700',
};

export default function SitesPage() {
  const queryClient = useQueryClient();
  const userRole = useUserRoleContext();
  const isAdmin = userRole === 'admin';

  const { data: sites = [], isLoading, error } = useQuery({
    queryKey: ['sites'],
    queryFn: () => apiClient.get<WpSiteListItem[]>('/sites').then((r) => r.data),
  });

  // Sync status and controls are only relevant for admins
  const { data: syncStatus } = useQuery({
    queryKey: ['sync-status'],
    queryFn: () => apiClient.get<SyncStatus>('/sites/sync/status').then((r) => r.data),
    refetchInterval: (query) => (query.state.data?.running ? 2000 : false),
    staleTime: 0,
    enabled: isAdmin,
  });

  // When running → done, refresh site cards
  const prevRunning = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (prevRunning.current === true && syncStatus?.running === false) {
      void queryClient.invalidateQueries({ queryKey: ['sites'] });
    }
    prevRunning.current = syncStatus?.running;
  }, [syncStatus?.running, queryClient]);

  const syncMutation = useMutation({
    mutationFn: () => apiClient.post('/sites/sync'),
    onSuccess: () => {
      // Immediately re-fetch status so polling activates
      void queryClient.invalidateQueries({ queryKey: ['sync-status'] });
    },
  });

  const isSyncing = syncMutation.isPending || (syncStatus?.running ?? false);

  const syncFailureMap = new Map(
    (syncStatus?.failedSites ?? []).map((f) => [f.siteId, f.error]),
  );

  const failCount = syncStatus?.failedSites.length ?? 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Sites</h2>
        {isAdmin && (
          <button
            onClick={() => void syncMutation.mutateAsync()}
            disabled={isSyncing}
            className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-60"
          >
            {isSyncing ? 'Syncing…' : 'Sync from WordPress'}
          </button>
        )}
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading sites…</p>}
      {error && <p className="text-sm text-red-600">Failed to load sites</p>}
      {isAdmin && syncMutation.isError && (
        <p className="text-sm text-red-600 mb-4">Could not start sync — please try again</p>
      )}

      {/* Sync summary bar */}
      {syncStatus?.completedAt && !syncStatus.running && (
        <div className={`mb-5 px-4 py-3 rounded-xl border text-sm flex items-center gap-3 ${
          failCount > 0
            ? 'bg-amber-50 border-amber-200 text-amber-800'
            : 'bg-green-50 border-green-200 text-green-800'
        }`}>
          <span>{failCount > 0 ? '⚠' : '✓'}</span>
          <span>
            Last sync completed{' '}
            <span className="font-medium">
              {new Date(syncStatus.completedAt).toLocaleTimeString()}
            </span>
            {' — '}
            {syncStatus.syncedPages} pages synced
            {failCount > 0 && `, ${failCount} site${failCount > 1 ? 's' : ''} could not be reached`}
          </span>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {sites.map((site) => (
          <div
            key={site.id}
            className={`bg-white border rounded-xl p-5 hover:shadow-sm transition-all ${
              syncFailureMap.has(site.id)
                ? 'border-amber-300 hover:border-amber-400'
                : 'border-gray-200 hover:border-brand-500'
            }`}
          >
            {(() => {
              const badgeStatus = getSiteBadgeStatus(site);

              return (
                <>
            {syncFailureMap.has(site.id) && (
              <div className="-mx-5 -mt-5 mb-4 px-4 py-2 bg-amber-50 border-b border-amber-200 rounded-t-xl flex items-start gap-2">
                <span className="text-amber-500 mt-0.5 shrink-0">⚠</span>
                <div>
                  <p className="text-xs font-medium text-amber-800">Sync failed</p>
                  <p className="text-xs text-amber-700 mt-0.5">{syncFailureMap.get(site.id)}</p>
                </div>
              </div>
            )}
            <p className="font-semibold text-gray-900">{site.name}</p>
            <p className="text-xs text-gray-500 mt-1 truncate">{site.url}</p>
            <p className="text-xs text-gray-400 mt-3">Blog ID: {site.blogId}</p>
            <p className="text-xs text-gray-500 mt-1">
              Pages/posts: <span className="text-gray-700">{site.totalPages}</span>
            </p>

            <div className="mt-3 space-y-1">
              <p className="text-xs text-gray-500">
                Last audit:{' '}
                <span className="text-gray-700">
                  {site.latestAuditAt ? new Date(site.latestAuditAt).toLocaleString() : 'Never'}
                </span>
              </p>
              {badgeStatus && (
                <div className="flex items-center gap-2 text-xs">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded font-medium ${STATUS_COLOURS[badgeStatus] ?? 'bg-gray-100 text-gray-600'}`}>
                    {badgeStatus}
                  </span>
                  <span className="text-gray-500">
                    {site.siteIssueCount} open issues
                  </span>
                  <span className="text-gray-400">
                    {site.auditedPages}/{site.totalPages} pages audited
                  </span>
                </div>
              )}
            </div>

            <div className="mt-4 flex items-center gap-3">
              <Link
                to={`/sites/${site.id}/review`}
                className="text-sm bg-brand-600 text-white px-3 py-1.5 rounded-lg hover:bg-brand-700"
              >
                Review site
              </Link>
              <Link
                to={`/sites/${site.id}/pages`}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                View pages
              </Link>
            </div>
                </>
              );
            })()}
          </div>
        ))}
      </div>
    </div>
  );
}

function getSiteBadgeStatus(site: WpSiteListItem) {
  if (site.latestAuditStatus === 'complete') {
    return site.fullyAudited ? 'complete' : 'partial';
  }

  return site.latestAuditStatus;
}

